import { profileSchema } from "@lib/auth/shared/profileSchema";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Form,
} from "@sfc/ui";
import { FormInput } from "@client/components/forms/form-input";
import { trpcVanilla } from "@client/trpc";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRightIcon } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { twMerge } from "tailwind-merge";
import { RouteScroller } from "@client/components/utils/RouteScroller";
import type * as z from "zod";

type FormValues = z.infer<typeof profileSchema>;

export type CompleteProfileProps = React.ComponentProps<"div">;

export function CompleteProfile({ className, ...rest }: CompleteProfileProps) {
  const [loading, setLoading] = useState(false);
  const form = useForm<FormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: "",
    },
  });

  async function onSubmit(values: FormValues) {
    if (loading) return;
    form.clearErrors("root");
    setLoading(true);
    try {
      await trpcVanilla.updateProfile.mutate({
        name: values.name,
      });
      // Reload to pick up the new session cookie with the user's name.
      // The Cookie Store API doesn't fire change events for httpOnly cookies,
      // so we need to reload to get AuthWrapper to re-read the session.
      window.location.reload();
    } catch (error) {
      console.error(error);
      form.setError("root", {
        message:
          "We couldn’t finish setting up your account. Please try again.",
      });
      setLoading(false);
    }
  }

  return (
    // The shell is viewport-locked, so this screen owns its scroller like any
    // route: centered when it fits, scrollable (not clipped) when it doesn't.
    <RouteScroller>
      <div
        data-application-theme="green"
        className={twMerge(
          "min-h-full flex items-center justify-center",
          className,
        )}
        {...rest}
      >
        <Form {...form}>
          <Card className="w-full max-w-[calc(100%-2rem)] sm:max-w-md shadow-xl text-center">
            <form
              aria-busy={loading}
              className="flex flex-col gap-6"
              onSubmit={form.handleSubmit(onSubmit)}
            >
              <CardHeader>
                <CardTitle>Welcome to Survival and Flourishing</CardTitle>
                <CardDescription>
                  Let's get you set up with a quick introduction. What should we
                  call you?
                </CardDescription>
              </CardHeader>
              <CardContent className="text-left">
                <FormInput
                  control={form.control}
                  name="name"
                  disabled={loading}
                  label="Name"
                  description="Your name will be used to identify you in rounds and when collaborating with team members."
                  placeholder="e.g., John Doe"
                />
                {loading && (
                  <p
                    role="status"
                    className="mt-4 text-sm text-muted-foreground"
                  >
                    Setting up your account. We’ll continue when it’s ready.
                  </p>
                )}
                {form.formState.errors.root && (
                  <p role="alert" className="mt-4 text-sm text-destructive">
                    {form.formState.errors.root.message}
                  </p>
                )}
              </CardContent>
              <CardFooter>
                <Button
                  className="flex-1"
                  size="lg"
                  type="submit"
                  isLoading={loading}
                >
                  Continue to S-Process <ArrowRightIcon />
                </Button>
              </CardFooter>
            </form>
          </Card>
        </Form>
      </div>
    </RouteScroller>
  );
}
